import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import {
  CellEditingStartedEvent,
  ColumnApi,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueParserParams,
} from '@ag-grid-community/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { OverlayService } from '@services/overlay.service';
import { finalize, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { MainQuery } from 'src/app/layouts/main-layout/state/main.query';
import {
  DistributionMode,
  DriverPatientGroup,
  EntityType,
  EventType,
  GqlService,
  listDriverPatientDistributionsQuery,
  PermissionType,
  UpdateDriverPatientDistributionInput,
  WorkflowStep,
} from '@services/gql.service';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { OrganizationService } from '@models/organization/organization.service';
import { OrganizationQuery } from '@models/organization/organization.query';
import { OrganizationStore } from '@models/organization/organization.store';
import { TrialsQuery } from '@models/trials/trials.query';
import { ExportType, Utils } from '@services/utils';
import { EventManager } from '@angular/platform-browser';
import { GuardWarningComponent } from '@components/guard-warning/guard-warning.component';
import { TimelineQuery } from 'src/app/pages/forecast-accruals-page/tabs/timeline-group/timeline/state/timeline.query';
import { TimelineService } from 'src/app/pages/forecast-accruals-page/tabs/timeline-group/timeline/state/timeline.service';
import { ApiService } from '@services/api.service';
import { PatientGroupsQuery } from 'src/app/pages/forecast-accruals-page/tabs/forecast/drivers/patients/patient-groups/state/patient-groups.query';
import { first, last, omit } from 'lodash-es';
import { AuthService } from '@models/auth/auth.service';
import { CanvasChart } from '@components/canvas-chart/canvas-chart.model';
import { ROUTING_PATH } from 'src/app/app-routing-path.const';
import {
  RemoveDialogComponent,
  RemoveDialogInput,
} from '@components/remove-dialog/remove-dialog.component';
import { PatientDriverUploadComponent } from './patient-driver-upload/patient-driver-upload.component';
import { ForecastAccrualsPageComponent } from '../../../../forecast-accruals-page.component';
import { PatientsService } from './state/patients.service';
import { PatientBlendedCurveModalComponent } from './patient-blended-curve-modal/patient-blended-curve-modal.component';
import { PatientCurveService } from '../../patient-curve/patient-curve.service';
import { PatientCurveQuery } from '../../patient-curve/patient-curve.query';
import { PatientGroupsService } from './patient-groups/state/patient-groups.service';
import { PatientCurveState, PatientCurveStore } from '../../patient-curve/patient-curve.store';
import { PatientsQuery } from './state/patients.query';
import { WorkflowQuery } from '../../../../../closing-page/tabs/quarter-close/close-quarter-check-list/store';
import { MessagesConstants } from '../../../../../../constants/messages.constants';
import { BlendedCurveModalDataModel } from '../models/blended-curve-modal-data.model';
import { EditableListDropdownItem } from '@components/editable-list-dropdown/editable-list-dropdown-item.model';
import { ForecastAndActualType, PatientCurvesConstants } from './patient-curves.constants';
import { TableConstants } from '@constants/table.constants';
import { TableService } from '@services/table.service';
import {
  decimalAdd,
  decimalDifference,
  decimalEquality,
  decimalRoundingToNumber,
  decimalRoundingToString,
} from '@utils/floating-math';
import { StickyElementService } from '@services/sticky-element.service';
import { batchPromises } from '@utils/batch-promises';

@UntilDestroy()
@Component({
  selector: 'aux-patient-curves',
  templateUrl: './patient-curves.component.html',
  styleUrls: ['patient-curves.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientCurvesComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly messagesConstants = MessagesConstants;

  @ViewChildren('patientDriverMenu') patientDriverMenu!: QueryList<TemplateRef<any>>;

  gridData$ = new BehaviorSubject<listDriverPatientDistributionsQuery[]>([]);

  isPatientsFinalized$ = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_CURVES
  );

  iCloseMonthsProcessing$ = this.mainQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);

  currentPatientGroupId = 0;

  multiChart$: Observable<CanvasChart> = this.gridData$.pipe(
    map((data) => {
      const labels = data.map(({ distribution_month }) =>
        Utils.dateFormatter(distribution_month, { day: undefined, year: '2-digit' })
      );
      return PatientCurvesConstants.multiChartOptions(data, labels);
    })
  );

  editCell = false;

  gridOptions = {
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        if (!params.editing) {
          switch (params.event.keyCode) {
            // delete button code 46 - backspace button code 8
            case 46:
            case 8:
              if (this.editMode$.getValue()) {
                TableService.clearCellRange(params, () => this.calculatePatientCurves());
              }

              return true;
            default:
              return false;
          }
        }
        return false;
      },
      editable: () => {
        return this.editMode$.getValue();
      },
      cellClass: (params) => PatientCurvesConstants.cellClass(params, this.editCell),
      cellClassRules: {
        'bg-aux-error': (params: any) => {
          let ret = false;
          switch (params.colDef.field) {
            case 'forecast.patients_discontinued':
            case 'forecast.patients_enrolled':
            case 'forecast.patients_complete':
              if (params.data.distribution_month === 'TOTAL' && !this.isBlended$.getValue()) {
                let diffEnrolledToDiscontinued = decimalDifference(
                  params.data.forecast.patients_enrolled,
                  params.data.forecast.patients_discontinued
                );
                ret = !decimalEquality(
                  diffEnrolledToDiscontinued,
                  params.data.forecast.patients_complete
                );
                this.isEqual$.next(!ret);
              }
              break;
            case 'forecast.net_patients_enrolled':
              if (
                !this.isBlended$.getValue() &&
                Utils.isNegativeAndNotCloseEnoughToZero(params.data.forecast.net_patients_enrolled)
              ) {
                ret = true;
                this.isNetPatientNegative$.next(true);
              }
              break;
            default:
              break;
          }

          return ret;
        },
      },
      cellStyle: (params) => {
        if (
          (params.colDef.field === 'forecast.cumulative_enrollment_percentage' ||
            params.colDef.field?.includes('actual') ||
            params.colDef.field?.includes('net_patients_enrolled') ||
            params.colDef.field?.includes('total_patients_enrolled')) &&
          params.data.distribution_month !== 'TOTAL'
        ) {
          return this.editCell
            ? { opacity: 0.7, 'justify-item': 'end' }
            : { 'justify-item': 'end' };
        }
        return {};
      },
    },
    ...PatientCurvesConstants.GRID_OPTIONS,
    ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
    columnDefs: PatientCurvesConstants.allColDefs(
      this.valueFormatter,
      this.numberParser,
      this.editCell,
      this.launchDarklyService.flags$.getValue().patient_drivers_edc
    ),
    excelStyles: PatientCurvesConstants.GRID_OPTIONS_EXEL_STYLES,
  } as GridOptions;

  gridColumnApi!: ColumnApi;

  gridApi!: GridApi;

  loading$ = new BehaviorSubject(false);

  isBlended$ = new BehaviorSubject(false);

  driverSettingId$ = new BehaviorSubject(null);

  showEnrollmentSettings$: Observable<boolean>;

  PatientCurveGroup: any[] = [];

  patientCurveGroups: DriverPatientGroup[] = [];

  patientCurveListOptions: EditableListDropdownItem[] = [];

  editingGridCell$ = new BehaviorSubject(false);

  selectedVendor = new UntypedFormControl('');

  curveControl = new FormControl('');

  clonedPatient: listDriverPatientDistributionsQuery[] = [];

  setTimelineSubscription!: Subscription;

  monthList: { start_date: string; end_date: Date | string } = { start_date: '', end_date: '' };

  btnLoading$ = new BehaviorSubject(false);

  editMode$ = new BehaviorSubject(false);

  isEqual$ = new BehaviorSubject(true);

  isNetPatientNegative$ = new BehaviorSubject(false);

  isFromScratch = false;

  userHasModifyPermissions = false;

  isInitialValueAlreadySet = false;

  curveEditedVal = '';

  constructor(
    private launchDarklyService: LaunchDarklyService,
    private overlayService: OverlayService,
    private forecastPage: ForecastAccrualsPageComponent,
    private gqlService: GqlService,
    private patientGroupsService: PatientGroupsService,
    private mainQuery: MainQuery,
    private patientService: PatientsService,
    private vendorsService: OrganizationService,
    public organizationQuery: OrganizationQuery,
    private organizationStore: OrganizationStore,
    private patientCurveService: PatientCurveService,
    public patientCurveQuery: PatientCurveQuery,
    private timelineService: TimelineService,
    private timelineQuery: TimelineQuery,
    private apiService: ApiService,
    private patientCurveStore: PatientCurveStore,
    public patientGroupsQuery: PatientGroupsQuery,
    private patientQuery: PatientsQuery,
    private trialsQuery: TrialsQuery,
    private eventManager: EventManager,
    private authService: AuthService,
    private workflowQuery: WorkflowQuery,
    private stickyElementService: StickyElementService
  ) {
    this.showEnrollmentSettings$ = this.launchDarklyService.select$(
      (flags) => flags.section_patient_driver_enrollment_settings
    );

    this.authService
      .isAuthorized$({
        sysAdminsOnly: false,
        permissions: [PermissionType.PERMISSION_MODIFY_PATIENT_CURVE],
      })
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        this.userHasModifyPermissions = x;
      });
  }

  ngOnDestroy(): void {
    this.stickyElementService.reset();
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    this.stickyElementService.configure();
  }

  ngOnInit() {
    this.vendorsService
      .get()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const vendors = this.organizationQuery.getAllVendors();
        if (vendors.length === 1) {
          this.organizationStore.setActive(vendors[0].id);
          this.selectedVendor.setValue(vendors[0].id);
        } else {
          // reset any older selected vendors.
          this.organizationStore.setActive(null);
          this.selectedVendor.setValue('');
        }
      });

    this.mainQuery
      .select('trialKey')
      .pipe(
        untilDestroyed(this),
        switchMap(() => {
          this.loading$.next(true);
          return combineLatest([this.patientCurveService.get(), this.patientGroupsService.get()]);
        }),
        tap(() => {
          this.isInitialValueAlreadySet = false;
          this.monthList.start_date = '';
          this.clonedPatient = Utils.clone([]);
          this.gridData$.next(this.clonedPatient);
        }),
        switchMap(() => {
          return combineLatest([
            this.patientCurveQuery.selectAll(),
            this.patientGroupsQuery.selectAll(),
          ]).pipe(
            map(([curves, patientGroups]) => {
              this.PatientCurveGroup = curves;
              const pgIds: String[] = [];
              curves.forEach((c) => {
                pgIds.push(c.patient_group_id);
              });
              const uniqPGs: any[] = [];
              patientGroups.forEach((pg) => {
                if (!pgIds.includes(pg.id)) {
                  uniqPGs.push({
                    constituent_patient_groups: [],
                    driver_setting_id: '',
                    is_blended: false,
                    name: pg.name,
                    patient_group_ids: [],
                    showLine: false,
                    organizations_with_forecasts: [],
                    patient_group_id: pg.id,
                  });
                }
              });
              const patientItems = [...curves, ...uniqPGs];

              this.updatePatientCurveList(patientItems);

              if (patientItems.length === 0) {
                this.curveControl.setValue('');
              } else if (
                this.curveControl.value !== patientItems[0].name &&
                !this.isInitialValueAlreadySet
              ) {
                this.curveControl.setValue(patientItems[0].name);
              } else if (this.curveEditedVal !== '') {
                this.curveControl.setValue(this.curveEditedVal);
                this.curveEditedVal = '';
              }
              return patientItems;
            })
          );
        })
      )
      .subscribe(() => {
        this.loading$.next(false);
        this.isInitialValueAlreadySet = true;
      });

    this.eventManager.addEventListener(document.body, 'keydown', (event: any) => {
      switch (event.code) {
        case 'Tab':
          this.gridApi.stopEditing();
          setTimeout(() => {
            const getCellCor = this.gridApi.getFocusedCell();
            this.gridApi.startEditingCell({
              rowIndex: getCellCor?.rowIndex || 0,
              // @ts-ignore
              colKey: getCellCor?.column?.colId || '',
            });
          }, 0);
          break;
        default:
          break;
      }
    });

    //temporary fix, should be investigated later
    combineLatest([this.curveControl.valueChanges, this.mainQuery.select('trialKey')])
      .pipe(untilDestroyed(this))
      .subscribe(([value]) => {
        const driverPatientGroup =
          this.patientCurveGroups?.find((item) => item.name === value) || null;
        this.selectCurve(driverPatientGroup);
      });
  }

  async resetPatientCurveGroup() {
    this.PatientCurveGroup = [];
  }

  ngAfterViewInit(): void {
    this.patientDriverMenu.changes
      .pipe(
        startWith(null),
        untilDestroyed(this),
        finalize(() => {
          setTimeout(() => {
            this.forecastPage.rightSideContainer.next(null);
          }, 0);
        })
      )
      .subscribe(() => {
        setTimeout(() => {
          this.forecastPage.rightSideContainer.next(this.patientDriverMenu.first || null);
        }, 0);
      });
  }

  async canDeactivate(): Promise<boolean> {
    if (this.editMode$.getValue()) {
      const result = this.overlayService.open({ content: GuardWarningComponent });
      const event = await result.afterClosed$.toPromise();
      return !!event.data;
    }
    return true;
  }

  valueFormatter(val: ValueFormatterParams) {
    const newValue = val.value === '0' ? 0 : val.value;
    return newValue ? decimalRoundingToString(newValue) : Utils.zeroHyphen;
  }

  numberParser(params: ValueParserParams): number | string {
    return Number.isNaN(Number(params.newValue))
      ? params.oldValue
      : Number(Math.abs(params.newValue));
  }

  onDataRendered({ api }: { api: GridApi }) {
    api.sizeColumnsToFit();
  }

  calculatePinnedBottomData() {
    const gridData = this.gridData$.getValue();

    const forecast = {
      patients_enrolled: 0,
      patients_discontinued: 0,
      patients_complete: 0,
      total_patients_enrolled: 0,
      net_patients_enrolled: 0,
      cumulative_enrollment_percentage: 0,
    };
    const actual = {
      patients_enrolled: 0,
      patients_discontinued: 0,
      patients_complete: 0,
      total_patients_enrolled: 0,
      net_patients_enrolled: 0,
      cumulative_enrollment_percentage: 0,
    };

    const columnsWithAggregation = (Object.keys(actual).filter((key) => {
      return key !== 'net_patients_enrolled';
    }) as unknown) as (keyof ForecastAndActualType)[];

    gridData.forEach((val) => {
      for (const key of columnsWithAggregation) {
        let currentValForForecast = forecast[key];
        let additionalValForForecast = val.forecast[key];

        let currentValForActual = actual[key];
        let additionalValForActual = val.actual ? val.actual[key] : 0;

        if (Utils.isNumber(currentValForForecast) && Utils.isNumber(additionalValForForecast)) {
          if ('total_patients_enrolled' === key) {
            forecast[key] = additionalValForForecast ? additionalValForForecast : 0; //always set total patients enrolled to last months value, not net
          } else {
            forecast[key] = decimalAdd(
              currentValForForecast,
              additionalValForForecast ? additionalValForForecast : 0
            );
          }
        }
        if (Utils.isNumber(currentValForActual) && Utils.isNumber(additionalValForActual)) {
          if ('total_patients_enrolled' === key) {
            actual[key] = additionalValForActual ? additionalValForActual : 0; //always set total patients enrolled to last months value, not net
          } else {
            actual[key] = decimalAdd(
              currentValForActual,
              additionalValForActual ? additionalValForActual : 0
            );
          }
        }
      }
      forecast.total_patients_enrolled = 0;
      actual.total_patients_enrolled = 0;
    });

    return { forecast, actual };
  }

  onGridReady(e: GridReadyEvent) {
    this.gridColumnApi = e.columnApi;
    this.gridApi = e.api;
    this.gridData$.pipe(untilDestroyed(this)).subscribe(() => {
      const pinnedBottomData = { ...this.calculatePinnedBottomData(), distribution_month: 'TOTAL' };
      this.gridApi.setPinnedBottomRowData([pinnedBottomData]);
    });
  }

  onPatientDriverUploadClick() {
    this.overlayService.open({ content: PatientDriverUploadComponent });
  }

  selectCurve(item: any): void {
    this.loading$.next(true);
    this.currentPatientGroupId = item?.patient_group_id;

    if (item) {
      if (this.editMode$.getValue()) {
        this.gridApi.stopEditing();
      }
      this.editMode$.next(false);
      this.editCell = false;
      if (item.driver_setting_id === '') {
        this.patientService
          .get(item.patient_group_id)
          .pipe(take(1))
          .subscribe(() => {
            this.populateGridDataWithTimeline();
          });

        this.isBlended$.next(false);
        this.isFromScratch = true;
      } else {
        this.isFromScratch = false;
        this.driverSettingId$.next(item.driver_setting_id);
        this.isBlended$.next(!!item?.is_blended);
        this.patientService
          .get(item.patient_group_id)
          .pipe(take(1))
          .subscribe(() => {
            this.clonePatient();
          });
      }
    }

    this.loading$.next(false);
  }

  populateGridDataWithTimeline(afterRemove = false) {
    this.timelineService.getTimelineItems().pipe(untilDestroyed(this)).subscribe();
    const patientQueryData = this.patientQuery.getAll();
    this.timelineQuery
      .select()
      .pipe(take(2))
      .subscribe(({ items }) => {
        const start_date = first(items)?.contract_start_date;
        let end_date = Utils.dateParse(`${last(items)?.contract_end_date.slice(0, 8)}01`);
        items.forEach((i) => {
          const parsedDate = Utils.dateParse(`${i.contract_end_date.slice(0, 8)}01`);
          if (parsedDate > end_date) {
            end_date = parsedDate;
          }
        });
        if (start_date) {
          this.monthList.start_date = start_date;
          this.monthList.end_date = end_date || '';
          if (afterRemove) {
            this.setGridData([]);
          } else {
            this.setGridData(patientQueryData);
          }
        }
        this.isFromScratch = true;
      });
  }

  editCurve(patientCurveGroup: any = null) {
    const driverPatientGroup = patientCurveGroup
      ? this.patientCurveGroups?.find((item) => item.name === patientCurveGroup.name)
      : null;

    let modalRef = this.overlayService.open({
      content: PatientBlendedCurveModalComponent,
      // @ts-ignore
      data: this.blendedCurveModalParams(driverPatientGroup),
    });

    modalRef.afterClosed$.pipe(untilDestroyed(this)).subscribe(({ data }) => {
      //cancel modal will be null
      if (data) {
        this.curveEditedVal = data.data?.name; //for edit reselection, name edit
        this.curveControl.setValue(data.data?.name); //for add selection
      }
    });
  }

  async deleteCurve(patientCurveGroup: EditableListDropdownItem) {
    const driverPatientGroup = this.patientCurveGroups.find(
      (item) => item.name === patientCurveGroup.name
    );

    if (!driverPatientGroup) {
      return;
    }
    const cannotDeleteMessage = `${driverPatientGroup.name} cannot be deleted because it is being used in other areas of the application.<br>Please first remove the dependencies below and then ${driverPatientGroup.name} can be successfully deleted.<br>`;

    let blendedCurves: any[] = [];
    if (!driverPatientGroup.is_blended) {
      const patientCurves = this.patientCurveQuery.getAll();

      blendedCurves = patientCurves.filter((el) =>
        el.constituent_patient_groups.find((c) => c.id === driverPatientGroup.patient_group_id)
      );
    }

    if (
      driverPatientGroup.is_blended &&
      driverPatientGroup.organizations_with_forecasts.length > 0
    ) {
      const routeInputs = [
        {
          componentName: 'Forecast Methodology:',
          name: driverPatientGroup.organizations_with_forecasts.map(({ name }) => name),
          link: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}`,
        },
      ];

      const removeDialogInput: RemoveDialogInput = {
        header: 'Remove Patient Curve',
        cannotDeleteMessage,
        routeInputs,
      };

      const resp = this.overlayService.open({
        content: RemoveDialogComponent,
        data: removeDialogInput,
      });

      await resp.afterClosed$.toPromise();
    } else if (
      !driverPatientGroup.is_blended &&
      (driverPatientGroup.organizations_with_forecasts.length > 0 || blendedCurves.length > 0)
    ) {
      const routeInputs = [
        {
          componentName: 'Blended Curves:',
          name: blendedCurves.map(({ name }) => name),
          link: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.PATIENT_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.PATIENT_DRIVER.CURVES}`,
        },
      ];

      if (driverPatientGroup.organizations_with_forecasts.length) {
        const routeInputsPatientGroup = [
          {
            componentName: 'Forecast Methodology:',
            name: driverPatientGroup.organizations_with_forecasts.map(({ name }) => name),
            link: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}`,
          },
        ];
        routeInputs.push(...routeInputsPatientGroup);
      }

      const removeDialogInput: RemoveDialogInput = {
        header: 'Remove Patient Curve',
        cannotDeleteMessage,
        routeInputs,
      };

      const resp = this.overlayService.open({
        content: RemoveDialogComponent,
        data: removeDialogInput,
      });

      await resp.afterClosed$.toPromise();
    } else {
      const resp = this.overlayService.openConfirmDialog({
        header: 'Remove Patient Curve',
        message: `Are you sure you want to remove Patient Curve ${driverPatientGroup.name}?`,
        okBtnText: 'Remove',
      });
      const event = await resp.afterClosed$.toPromise();

      if (event.data?.result) {
        this.curveControl.setValue('');
        await this.patientCurveService.remove(driverPatientGroup);
        const { data: patientCurveServiceData } = await this.patientCurveService
          .get()
          .pipe(take(1))
          .toPromise();
        if (patientCurveServiceData) {
          this.PatientCurveGroup = patientCurveServiceData;
        }
        this.gridData$.next([]);
        this.gridOptions.api?.refreshCells({ force: true });
        this.populateGridDataWithTimeline(true);
      }
    }
  }

  async onExportForPatientDriver() {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';

    if (this.btnLoading$.getValue()) {
      return;
    }
    this.btnLoading$.next(true);

    const { success, errors } = await this.gqlService
      .processEvent$({
        type: EventType.GENERATE_EXPORT,
        entity_type: EntityType.TRIAL,
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        payload: JSON.stringify({
          export_type: ExportType.PATIENT_CURVE,
          filename: `${trialName}_patient_curves`,
        }),
      })
      .toPromise();
    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }
    this.btnLoading$.next(false);
  }

  calculatePatientCurves(): void {
    let totalNetPatientEnrolled = 0;
    let sumOfNetPatientEnrolled = 0;
    let patientEnrolledTotal = 0;
    let isNetPatientNegative = false;

    this.gridData$.getValue().forEach((x: listDriverPatientDistributionsQuery) => {
      x.forecast.patients_enrolled = decimalRoundingToNumber(x.forecast.patients_enrolled || 0, 8);
      x.forecast.patients_discontinued = decimalRoundingToNumber(
        x.forecast.patients_discontinued || 0,
        8
      );
      x.forecast.patients_complete = decimalRoundingToNumber(x.forecast.patients_complete || 0, 8);

      let enrolledLessDiscontinued = decimalDifference(
        x.forecast.patients_enrolled,
        x.forecast.patients_discontinued
      );

      let lessPatientsComplete = decimalDifference(
        enrolledLessDiscontinued,
        x.forecast.patients_complete
      );
      totalNetPatientEnrolled = decimalAdd(totalNetPatientEnrolled, lessPatientsComplete);
      sumOfNetPatientEnrolled = decimalAdd(sumOfNetPatientEnrolled, totalNetPatientEnrolled);

      x.forecast.net_patients_enrolled = decimalRoundingToNumber(totalNetPatientEnrolled, 8);
      patientEnrolledTotal = decimalAdd(patientEnrolledTotal, x.forecast.patients_enrolled);
      x.forecast.total_patients_enrolled = decimalRoundingToNumber(patientEnrolledTotal, 8);

      isNetPatientNegative = Utils.isNegativeAndNotCloseEnoughToZero(
        x.forecast.net_patients_enrolled
      );
    });
    this.isNetPatientNegative$.next(isNetPatientNegative);

    this.gridData$.getValue().forEach((x: listDriverPatientDistributionsQuery) => {
      x.forecast.cumulative_enrollment_percentage = Utils.calculateAsPercentage(
        sumOfNetPatientEnrolled,
        x.forecast.net_patients_enrolled || 0
      );
    });

    this.gridData$.next(this.gridData$.getValue());
    this.gridApi.refreshCells();
  }

  private showErrors(): boolean {
    const rowNodes: IRowNode[] = [];
    let isThereAnyInvalidRow = false;
    this.gridApi.forEachNode((node) => {
      const pddq = node.data as listDriverPatientDistributionsQuery;
      const { patients_enrolled, patients_discontinued, patients_complete } = pddq.forecast;

      // @ts-ignore
      // eslint-disable-next-line no-restricted-globals
      if (
        Utils.isNumber(patients_enrolled) &&
        Utils.isNumber(patients_discontinued) &&
        Utils.isNumber(patients_complete)
      ) {
        if (node.data.showError) {
          // eslint-disable-next-line no-param-reassign
          node.data.showError = false;
          rowNodes.push(node);
        }
      } else {
        isThereAnyInvalidRow = true;
        // eslint-disable-next-line no-param-reassign
        node.data.showError = true;
        rowNodes.push(node);
      }
    });

    this.gridApi.redrawRows({ rowNodes });
    return isThereAnyInvalidRow;
  }

  async onSaveAll() {
    this.btnLoading$.next(true);
    const isInvalid = this.showErrors();
    if (isInvalid) {
      this.btnLoading$.next(false);
      this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);
      return;
    }

    if (!isInvalid) {
      if (this.isFromScratch) {
        await this.processPatientsCreatedFromScratch();
      } else {
        this.patientCurveStore.setLoading(true);

        const gData: UpdateDriverPatientDistributionInput[] = this.gridData$.getValue().map((x) => {
          return {
            id: x.forecast.id || '',
            distribution_month: x.distribution_month,
            cumulative_enrollment_percentage: x.forecast.cumulative_enrollment_percentage,
            distribution_mode: x.forecast.distribution_mode,
            net_patients_enrolled: x.forecast.net_patients_enrolled,
            patients_complete: x.forecast.patients_complete,
            patients_discontinued: x.forecast.patients_discontinued,
            patients_enrolled: x.forecast.patients_enrolled,
            total_patients_enrolled: x.forecast.total_patients_enrolled,
          };
        });

        const responses = await batchPromises(gData, (p) =>
          this.patientService.updatePatientDistribution(p)
        );

        if (!responses.every((x) => x)) {
          this.btnLoading$.next(false);
          this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);
          return;
        }
        this.overlayService.success('Patients Table updated successfully!');

        const { success, errors } = await this.gqlService
          .processEvent$({
            type: EventType.PATIENT_DRIVER_DISTRIBUTION_UPDATED,
            entity_type: EntityType.DRIVER,
            entity_id:
              this.currentPatientGroupId === null || this.currentPatientGroupId === 0
                ? ''
                : this.currentPatientGroupId.toString(),
          })
          .toPromise();

        if (success) {
          this.overlayService.success();
        } else {
          this.overlayService.error(errors);
        }
        await this.cancelEditMode();
      }
    }
    this.editMode$.next(false);
    this.btnLoading$.next(false);
  }

  async processPatientsCreatedFromScratch() {
    const thingsToUpdate: any[] = [];
    this.gridApi.forEachNode((node) => {
      thingsToUpdate.push(node.data);
    });
    const data = thingsToUpdate.map((row) => ({
      month: Utils.dateFormatter(row.distribution_month, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      }),

      patients_enrolled: row.forecast.patients_enrolled,
      patients_discontinued: row.forecast.patients_discontinued,
      patients_complete: row.forecast.patients_complete,
      total_patients_enrolled: row.forecast.total_patients_enrolled,
      net_patients_enrolled: row.forecast.net_patients_enrolled,
      cumulative_enrollment_percentage: row.forecast.cumulative_enrollment_percentage,
    }));
    const key = `${this.getFilePath(
      this.curveControl.value !== '' ? this.currentPatientGroupId.toString() : 'Patient-Curve'
    )}patient-curve-from-scratch.csv`;
    const blob = new Blob([Utils.objectToCsv(data)], { type: 'text/csv' });
    const fileSuccess = await this.apiService.uploadFile(key, new File([blob], key));

    if (fileSuccess) {
      const { success, errors } = await this.gqlService
        .processEvent$({
          type: EventType.PATIENT_DRIVER_TEMPLATE_UPLOADED,
          entity_type: EntityType.DRIVER,
          entity_id: this.curveControl.value !== '' ? this.currentPatientGroupId.toString() : '',
          bucket_key: `public/${key}`,
          payload: JSON.stringify({
            should_show_on_document_library: false,
          }),
        })
        .toPromise();

      if (success) {
        this.loading$.next(true);
        // allows time for PATIENT_DRIVER_TEMPLATE_UPLOADED event to complete
        await new Promise((resolve) => setTimeout(resolve, 5000));
        this.overlayService.success();
        this.isFromScratch = false;
        await this.resetPatientCurveGroup();
        await this.patientGroupsService.get().pipe(take(1)).toPromise();
        await this.patientService
          .get(this.curveControl.value === '' ? undefined : this.currentPatientGroupId.toString())
          .pipe(take(1))
          .toPromise();
        if (this.curveControl.value === '') {
          this.curveControl.setValue('Patient Curve');
        }
        this.clonePatient();
        this.gridApi.stopEditing();
        this.editMode$.next(false);
        this.editCell = false;
        this.gridOptions.api?.refreshCells({ force: true });
        this.loading$.next(false);
      } else {
        this.overlayService.error(errors);
      }
    } else {
      this.overlayService.error(MessagesConstants.FILE.ERROR_UPLOADING_FILE);
    }
  }

  setGridData(pData: listDriverPatientDistributionsQuery[]) {
    const { start_date } = this.monthList;
    const { end_date } = this.monthList;
    this.clonedPatient = [];
    if (start_date) {
      let date = Utils.dateParse(`${start_date.slice(0, 8)}01`);
      let index = 0;
      const month_list: { label: string; value: string }[] = [];

      while (date <= end_date) {
        const value = new Intl.DateTimeFormat('fr-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(date);
        date = Utils.addMonths(date, 1);
        if (pData.length !== 0) {
          if (!pData.some((x) => x.distribution_month === value)) {
            const l: listDriverPatientDistributionsQuery = {
              __typename: 'DriverPatientDistribution',
              distribution_month: value,
              forecast: {
                __typename: 'PatientEnrollmentData',
                id: uuidv4(),
                patients_enrolled: 0,
                patients_discontinued: 0,
                patients_complete: 0,
                net_patients_enrolled: 0,
                cumulative_enrollment_percentage: 0,
                total_patients_enrolled: 0,
                distribution_mode: DistributionMode.DISTRIBUTION_MODE_FORECAST,
              },
              actual: null,
            };
            this.patientService.createPatient({
              ...omit(l.forecast, ['id', '__typename']),
              driver_patient_distribution_id: l.forecast.id,
              distribution_month: l.distribution_month,
              driver_setting_id: this.driverSettingId$.getValue() || '',
            });
            this.clonedPatient.push(l);
          } else {
            this.clonedPatient.push(pData[index]);
          }
        } else {
          month_list.push({
            value,
            label: Utils.dateFormatter(value, { day: undefined, year: '2-digit' }),
          });

          const l: listDriverPatientDistributionsQuery = {
            __typename: 'DriverPatientDistribution',
            distribution_month: value,
            forecast: {
              __typename: 'PatientEnrollmentData',
              id: uuidv4(),
              patients_enrolled: 0,
              patients_discontinued: 0,
              patients_complete: 0,
              net_patients_enrolled: 0,
              cumulative_enrollment_percentage: 0,
              total_patients_enrolled: 0,
              distribution_mode: DistributionMode.DISTRIBUTION_MODE_FORECAST,
            },
            actual: null,
          };
          if (this.clonedPatient.length < month_list.length) {
            this.clonedPatient.push(l);
          }
        }
        index += 1;
      }
      const newData = Utils.clone(this.clonedPatient);
      newData.forEach((x) => {
        x.forecast.net_patients_enrolled = decimalRoundingToNumber(
          x.forecast.net_patients_enrolled || 0,
          8
        );
        x.forecast.cumulative_enrollment_percentage = decimalRoundingToNumber(
          x.forecast.cumulative_enrollment_percentage || 0,
          8
        );
      });
      this.gridData$.next(newData);
    }
  }

  getFilePath(id: string) {
    const trialId = this.mainQuery.getValue().trialKey;
    return `trials/${trialId}/sites/${id}/patient-driver/`;
  }

  editGrid() {
    this.editCell = true;
    this.gridOptions.api?.refreshCells({ force: true });

    this.gridApi.startEditingCell({
      rowIndex: 0,
      colKey: 'forecast.patients_enrolled',
    });
  }

  async cancelEditMode() {
    if (this.isFromScratch) {
      this.populateGridDataWithTimeline();
    } else {
      this.clonePatient();
    }
    this.gridApi.stopEditing(true);
    this.editMode$.next(false);
    this.isNetPatientNegative$.next(false);
    this.editCell = false;
    this.gridOptions.api?.refreshCells({ force: true });
  }

  async saveEditMode() {
    const isEqual = this.isEqual$.getValue();
    if (!isEqual) {
      this.overlayService.error(
        'This patient curve is unable to be saved until the following issue is resolved: Patients Enrolled must be equal to the sum of Patients Complete and Patients Discontinued.'
      );
      return;
    }
    await this.onSaveAll();
    this.gridApi.stopEditing(false);
  }

  cellValueChanged() {
    this.calculatePatientCurves();
  }

  cellEditingStopped() {
    this.editingGridCell$.next(false);
  }

  rowPinnedCheck(event: CellEditingStartedEvent) {
    if (event.node.rowPinned) {
      this.gridOptions.api?.stopEditing();
    } else {
      this.editingGridCell$.next(true);
    }
  }

  clonePatient() {
    const pData = this.patientQuery.getAll();
    if (!this.monthList.start_date) {
      if (this.setTimelineSubscription) {
        this.setTimelineSubscription.unsubscribe();
      }
      this.setTimelineSubscription = this.timelineService
        .getTimelineItems()
        .pipe(untilDestroyed(this))
        .subscribe();
      this.timelineQuery
        .select()
        .pipe(take(2))
        .subscribe(({ items }) => {
          const start_date = first(items)?.contract_start_date;
          let end_date = Utils.dateParse(`${last(items)?.contract_end_date.slice(0, 8)}01`);
          items.forEach((i) => {
            const parsedDate = Utils.dateParse(`${i.contract_end_date.slice(0, 8)}01`);
            if (parsedDate > end_date) {
              end_date = parsedDate;
            }
          });
          if (start_date) {
            this.monthList.start_date = start_date || '';
            this.monthList.end_date = end_date || '';
            this.setGridData(pData);
          }
        });
    } else {
      this.patientCurveService.get().pipe(untilDestroyed(this)).subscribe();
      this.setGridData(pData);
    }
  }

  onEditClick(): void {
    this.editMode$.next(true);
    this.editGrid();
  }

  private blendedCurveModalParams(item: DriverPatientGroup): BlendedCurveModalDataModel {
    return {
      availableGroups: this.PatientCurveGroup.filter(
        (group) => !group.is_blended && group.patient_group_id
      ).filter(
        (value, index, self) =>
          index === self.findIndex((group) => group.driver_setting_id === value.driver_setting_id)
      ),
      text: {
        title: 'Blended Patient Curve',
        subTitle: 'Select Patient Groups',
      },
      blendedCurve: item,
    };
  }

  private updatePatientCurveList(items: DriverPatientGroup[]): void {
    this.patientCurveGroups = items;
    this.patientCurveListOptions = items.map((item: PatientCurveState) => ({
      name: item.name,
      value: item.name,
      showLine: item.showLine,
      isEditable: item.is_blended,
      isDeletable: item.driver_setting_id.length > 1,
    }));
  }
}
